import type { AssignedWorkoutTree } from "./assignedWorkoutTree";
import { EQUIPMENT_AMBIENT, EQUIPMENT_LABEL } from "./equipment";
import { phoneSectionName } from "./briefing";
import type { PreviewSection } from "./workoutPreview";

/* 🔴 W66 — THE ASSIGN WINDOW'S PHONE IS THE CLIENT'S WORKOUT OVERVIEW (his word, 2026-09-24):
 * *"it needs to look EXACTLY like the client sees this workout, in the workout overview. same kind
 * of screen ratio too … iphone 18 pro or pro max."* The phone's screen is `PreWorkoutBriefingView`:
 * back + settings discs, the title, DESCRIPTION, EQUIPMENT chips, one card per section with a
 * letter tile, the name and the dose per row, a dotted rail with a dot per row, and the Start
 * workout pill. Everything here is DERIVED from what the phone derives it from — the tree, the
 * equipment vocabulary, the phone's own section-name rule (`phoneSectionName`) — never typed a second time. */

/** iPhone 17/18 Pro, in points. The frame keeps this ratio at any width. */
export const PHONE_POINTS = { width: 402, height: 874 } as const;

/** The exercise ids the tree names, in order of first appearance — the equipment query's input. */
export function exerciseIdsOf(tree: AssignedWorkoutTree): string[] {
  const seen = new Set<string>(); const out: string[] = [];
  for (const s of tree.coach_workout_sections ?? []) {
    for (const e of s.coach_workout_exercises ?? []) {
      if (e.exercise_id !== null && !seen.has(e.exercise_id)) { seen.add(e.exercise_id); out.push(e.exercise_id); }
    }
  }
  return out;
}

/** The chips the phone draws: every key any exercise in the workout carries, once, in the order the
 *  workout first needs it; a key the vocabulary cannot name is shown as its own humanised words. */
export function equipmentChips(
  tree: AssignedWorkoutTree,
  equipmentByExercise: ReadonlyMap<string, readonly string[]>,
): { key: string; label: string }[] {
  const seen = new Set<string>(); const out: { key: string; label: string }[] = [];
  for (const id of exerciseIdsOf(tree)) {
    for (const key of equipmentByExercise.get(id) ?? []) {
      // The phone draws no chip for what every gym has (`EQUIPMENT_AMBIENT`, the phone's own list).
      if (seen.has(key) || EQUIPMENT_AMBIENT.includes(key)) continue;
      seen.add(key);
      out.push({ key, label: EQUIPMENT_LABEL[key] ?? humanise(key) });
    }
  }
  return out;
}

function humanise(key: string): string {
  const words = key.replace(/^custom_/, "").replace(/_[a-z0-9]{6}$/, "").split(/[_\s]+/).filter(Boolean);
  return words.map((w) => w[0]!.toUpperCase() + w.slice(1)).join(" ");
}

/** The card's header word: the section's own name, else the PHONE's rule (`MAIN` / `Superset`) — what the client sees,
 *  never a numbered name (W104). */
export function phoneSectionTitle(section: PreviewSection): string {
  return section.implicit ? section.formatLabel : phoneSectionName(section.rawName, section.format, false);
}

/** The letter on the tile — the first letter of the name, as the phone draws it. */
export function tileLetter(name: string): string {
  const c = name.trim().charAt(0);
  return c === "" ? "·" : c.toUpperCase();
}
